import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchPages,
  type PagesQuery,
  type PagesResponse,
} from '../api/pages';
import './PagesPage.css';

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!ms || Number.isNaN(ms)) return '—';
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

function qualityBucket(q: number | null): string {
  if (q === null || q === undefined) return '—';
  if (q < 0.5) return 'stub';
  if (q < 0.7) return 'drafted';
  if (q < 0.9) return 'mature';
  return 'canonical';
}

export function PagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('id');
  const [resp, setResp] = useState<PagesResponse | null>(null);
  const [query, setQuery] = useState<PagesQuery>({});
  const [titleQuery, setTitleQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(initialId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    const merged: PagesQuery = {
      ...query,
      q: titleQuery.trim() || undefined,
    };
    fetchPages(merged)
      .then((r) => {
        setResp(r);
        if (initialId && r.pages.some((p) => p.id === initialId)) {
          setSelected(initialId);
          searchParams.delete('id');
          setSearchParams(searchParams, { replace: true });
        } else if (r.pages.length > 0 && !selected) {
          setSelected(r.pages[0]!.id);
        } else if (r.pages.length === 0) {
          setSelected(null);
        }
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const selectedPage = useMemo(
    () => resp?.pages.find((p) => p.id === selected) ?? null,
    [resp, selected],
  );

  const handleTitleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    load();
  };

  const setFilter = <K extends keyof PagesQuery>(
    key: K,
    value: PagesQuery[K],
  ) => {
    setQuery((q) => ({ ...q, [key]: value }));
  };

  return (
    <div className="pages-page">
      <header className="pages-page__header">
        <div>
          <div className="pages-page__title">Pages</div>
          <div className="pages-page__subtitle">
            {resp
              ? `${resp.count} · ${resp.requires_review_count} need review · ` +
                `${resp.with_broken_citations_count} with broken cites`
              : '—'}
          </div>
        </div>
        <button
          type="button"
          className="pages-page__refresh"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      <div className="pages-page__filters">
        <label className="pages-page__filter">
          nature
          <select
            value={query.nature ?? 'any'}
            onChange={(e) =>
              setFilter('nature', e.target.value as PagesQuery['nature'])
            }
          >
            <option value="any">any</option>
            <option value="source-grounded">source-grounded</option>
            <option value="synthesis">synthesis</option>
          </select>
        </label>
        <label className="pages-page__filter">
          requires review
          <select
            value={query.requires_review ?? 'any'}
            onChange={(e) =>
              setFilter(
                'requires_review',
                e.target.value as PagesQuery['requires_review'],
              )
            }
          >
            <option value="any">any</option>
            <option value="true">yes</option>
            <option value="false">no</option>
          </select>
        </label>
        <label className="pages-page__filter">
          broken cites
          <select
            value={query.has_broken_citations ?? 'any'}
            onChange={(e) =>
              setFilter(
                'has_broken_citations',
                e.target.value as PagesQuery['has_broken_citations'],
              )
            }
          >
            <option value="any">any</option>
            <option value="true">has broken</option>
            <option value="false">none</option>
          </select>
        </label>
        <form
          className="pages-page__search"
          onSubmit={handleTitleSubmit}
        >
          <input
            type="text"
            placeholder="title contains..."
            value={titleQuery}
            onChange={(e) => setTitleQuery(e.target.value)}
            className="pages-page__search-input"
          />
          <button type="submit" className="pages-page__search-submit">
            Find
          </button>
        </form>
      </div>

      {error && <div className="pages-page__error">{error}</div>}

      <div className="pages-page__split">
        <aside className="pages-page__list">
          {loading && !resp && (
            <div className="pages-page__status">Loading...</div>
          )}
          {resp && resp.pages.length === 0 && !loading && (
            <div className="pages-page__status">
              No pages match the filters.
            </div>
          )}
          {resp?.pages.map((p) => (
            <button
              key={p.id}
              type="button"
              className={
                'pages-page__row' +
                (p.id === selected ? ' pages-page__row--active' : '') +
                (p.requires_review ? ' pages-page__row--flagged' : '')
              }
              onClick={() => setSelected(p.id)}
            >
              <div className="pages-page__row-title">{p.title || '(untitled)'}</div>
              <div className="pages-page__row-meta">
                {p.nature && (
                  <span className={`pages-page__tag pages-page__tag--${p.nature}`}>
                    {p.nature}
                  </span>
                )}
                <span>{qualityBucket(p.quality)}</span>
                {p.cited_sources.length > 0 && (
                  <span>{p.cited_sources.length} cite{p.cited_sources.length === 1 ? '' : 's'}</span>
                )}
                {p.requires_review && (
                  <span className="pages-page__tag pages-page__tag--review">
                    review
                  </span>
                )}
                {p.broken_citations.length > 0 && (
                  <span className="pages-page__tag pages-page__tag--broken">
                    {p.broken_citations.length} broken
                  </span>
                )}
              </div>
            </button>
          ))}
        </aside>

        <section className="pages-page__detail">
          {!selectedPage && (
            <div className="pages-page__status">Select a page.</div>
          )}
          {selectedPage && (
            <>
              <div className="pages-page__detail-head">
                <div className="pages-page__detail-title">
                  {selectedPage.title || '(untitled)'}
                </div>
                {selectedPage.url && (
                  <a
                    href={selectedPage.url}
                    target="_blank"
                    rel="noreferrer"
                    className="pages-page__detail-link"
                  >
                    open in Outline ↗
                  </a>
                )}
              </div>
              <dl className="pages-page__detail-meta">
                <dt>id</dt>
                <dd>
                  <code>{selectedPage.id}</code>
                </dd>
                {selectedPage.domain && (
                  <>
                    <dt>domain</dt>
                    <dd>
                      {selectedPage.domain}
                      {selectedPage.subdomain && ` / ${selectedPage.subdomain}`}
                      {selectedPage.cluster && ` (cluster: ${selectedPage.cluster})`}
                    </dd>
                  </>
                )}
                {selectedPage.nature && (
                  <>
                    <dt>nature</dt>
                    <dd>{selectedPage.nature}</dd>
                  </>
                )}
                {selectedPage.quality !== null && (
                  <>
                    <dt>quality</dt>
                    <dd>
                      {selectedPage.quality.toFixed(2)} ({qualityBucket(selectedPage.quality)})
                    </dd>
                  </>
                )}
                <dt>updated</dt>
                <dd>{formatRelativeTime(selectedPage.updated_at)}</dd>
                {selectedPage.requires_review && (
                  <>
                    <dt>requires_review</dt>
                    <dd className="pages-page__warn">true</dd>
                  </>
                )}
                {selectedPage.broken_citations.length > 0 && (
                  <>
                    <dt>broken_citations</dt>
                    <dd className="pages-page__warn">
                      {selectedPage.broken_citations.map((s) => (
                        <code key={s} className="pages-page__chip">{s}</code>
                      ))}
                    </dd>
                  </>
                )}
                {selectedPage.source_ids.length > 0 && (
                  <>
                    <dt>source_ids</dt>
                    <dd>
                      {selectedPage.source_ids.map((s) => (
                        <code key={s} className="pages-page__chip">{s}</code>
                      ))}
                    </dd>
                  </>
                )}
                {selectedPage.cited_sources.length > 0 && (
                  <>
                    <dt>cited_sources</dt>
                    <dd>
                      {selectedPage.cited_sources.map((s) => (
                        <code key={s} className="pages-page__chip">{s}</code>
                      ))}
                    </dd>
                  </>
                )}
              </dl>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
